import {
  fishydexBrowserStorage,
  removeAndVerify,
  verifyStoredValue,
} from "./browser-storage-provider.js";

const EVIDENCE_SCHEMA = "fishystuff.fishydex-guide-evidence.v1";
const ACTIVE_SCHEMA = "fishystuff.fishydex-guide-active-reference.v1";
const SLOT = "__fishystuffFishydexGuideEvidenceV1";
// These are domain limits, not estimates of a particular browser's storage
// quota: one snapshot can cover substantially more species than the live
// catalog, and one local evidence document can retain 32 explicit imports.
const MAX_SNAPSHOT_ENTRIES = 2_048;
const MAX_EVIDENCE_ROWS = 32;
const MAX_EVIDENCE_RAW_CHARS = 3_500_000;
const MAX_ACTIVE_RAW_CHARS = 2_048;
const MAX_OPERATION_ID_CHARS = 160;

export const FISHYDEX_GUIDE_EVIDENCE_KEY = "fishystuff.fishydex.guide-evidence.v1";
export const FISHYDEX_GUIDE_ACTIVE_REFERENCE_KEY =
  "fishystuff.fishydex.guide-active-reference.v1";
export const FISHYDEX_GUIDE_EVIDENCE_BRAND = Symbol.for(
  "fishystuff.fishydex-guide-evidence.v1",
);

function exactRecord(value, fields, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const actual = Object.keys(value).sort();
  const expected = fields.slice().sort();
  if (actual.length !== expected.length
      || actual.some((field, index) => field !== expected[index])) {
    throw new TypeError(`${label} has unsupported or missing fields`);
  }
  return value;
}

function exactOptions(value, fields, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const allowed = new Set(fields);
  if (Object.keys(value).some((field) => !allowed.has(field))) {
    throw new TypeError(`${label} has unsupported fields`);
  }
  return value;
}

function clone(value) {
  return value === undefined ? undefined : structuredClone(value);
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }
  for (const child of Object.values(value)) {
    deepFreeze(child);
  }
  return Object.freeze(value);
}

function frozenClone(value) {
  return deepFreeze(clone(value));
}

function positiveRevision(value, label) {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new TypeError(`${label} revision must be a positive safe integer`);
  }
  return value;
}

function expectedRevision(value, label = "Fish Guide expected") {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${label} revision must be a non-negative safe integer`);
  }
  return value;
}

function operationId(value, prefix) {
  if (value !== undefined) {
    if (
      typeof value !== "string"
      || !value
      || value !== value.trim()
      || value.length > MAX_OPERATION_ID_CHARS
      || /[\u0000-\u001f\u007f]/.test(value)
    ) {
      throw new TypeError("Fish Guide operation ID must be bounded canonical text");
    }
    return value;
  }
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid
    ? `${prefix}.${uuid}`
    : `${prefix}.${Date.now().toString(36)}.${Math.random().toString(36).slice(2)}`;
}

function commandOptions(value, label, prefix) {
  const parsed = exactOptions(value, ["expectedRevision", "operationId"], label);
  return {
    operationId: operationId(parsed.operationId, prefix),
    expectedRevision: parsed.expectedRevision === undefined
      ? undefined
      : expectedRevision(parsed.expectedRevision, label),
  };
}

function evidenceId(value, { nullable = false } = {}) {
  if (nullable && value === null) {
    return null;
  }
  if (
    typeof value !== "string"
    || !/^guide_evidence_[0-9a-f]{32}$/.test(value)
  ) {
    throw new TypeError("Fish Guide evidence ID must be an opaque v1 identifier");
  }
  return value;
}

function canonicalSnapshot(snapshot) {
  return JSON.stringify(snapshot);
}

function boundedSnapshot(snapshot) {
  if (snapshot.entries.length > MAX_SNAPSHOT_ENTRIES) {
    throw new TypeError(
      `Fish Guide snapshot exceeds the v1 ${MAX_SNAPSHOT_ENTRIES}-entry limit`,
    );
  }
  return snapshot;
}

function commandResult(projection, fields) {
  return frozenClone({ ...projection, ...fields });
}

function snapshotIdentity(snapshot) {
  return JSON.stringify([
    snapshot.subject.serviceRegion,
    snapshot.subject.familyId,
    snapshot.observedAt,
    snapshot.client.sha256,
  ]);
}

function createEvidenceId() {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) {
    return `guide_evidence_${uuid.replaceAll("-", "")}`;
  }
  if (typeof globalThis.crypto?.getRandomValues !== "function") {
    throw new Error("Secure randomness is unavailable for Fish Guide evidence identity");
  }
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return `guide_evidence_${Array.from(
    bytes,
    (value) => value.toString(16).padStart(2, "0"),
  ).join("")}`;
}

function requireStorage(storage) {
  for (const method of ["getItem", "setItem", "removeItem"]) {
    if (typeof storage?.[method] !== "function") {
      throw new TypeError(`Fish Guide evidence storage is missing ${method}()`);
    }
  }
  return storage;
}

function invalid(raw, reason) {
  return { kind: "invalid", raw, reason };
}

function unsupported(raw, reason) {
  return { kind: "unsupportedVersion", raw, reason };
}

function parseJson(raw, label, maxChars) {
  if (raw === null) {
    return { kind: "missing", raw: null };
  }
  if (typeof raw !== "string") {
    return invalid(raw, `${label} must be stored as text`);
  }
  if (raw === "") {
    return invalid(raw, `${label} must be non-empty JSON`);
  }
  if (raw.length > maxChars) {
    return invalid(raw, `${label} exceeds its v1 raw-size limit`);
  }
  let value;
  try {
    value = JSON.parse(raw);
  } catch (_error) {
    return invalid(raw, `${label} must be JSON`);
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return invalid(raw, `${label} must be an object`);
  }
  return { kind: "parsed", raw, value };
}

function decodeEvidence(raw, fishStateContract) {
  const parsed = parseJson(raw, "Fish Guide evidence", MAX_EVIDENCE_RAW_CHARS);
  if (parsed.kind !== "parsed") {
    return parsed;
  }
  if (parsed.value.schema !== EVIDENCE_SCHEMA) {
    return unsupported(raw, "Fish Guide evidence schema is unsupported");
  }
  try {
    const document = exactRecord(
      parsed.value,
      ["schema", "revision", "evidence"],
      "Fish Guide evidence",
    );
    if (!Array.isArray(document.evidence)) {
      throw new TypeError("Fish Guide evidence rows must be an array");
    }
    if (document.evidence.length > MAX_EVIDENCE_ROWS) {
      throw new TypeError(
        `Fish Guide evidence exceeds the v1 ${MAX_EVIDENCE_ROWS}-row limit`,
      );
    }
    const seenIds = new Set();
    const seenIdentities = new Map();
    const evidence = document.evidence.map((entry, index) => {
      const row = exactRecord(entry, ["id", "snapshot"], `Fish Guide evidence row ${index}`);
      const snapshot = boundedSnapshot(
        fishStateContract.normalizeFishGuideSnapshot(row.snapshot),
      );
      const id = evidenceId(row.id);
      if (seenIds.has(id)) {
        throw new TypeError("Fish Guide evidence ID is duplicated");
      }
      seenIds.add(id);
      const content = canonicalSnapshot(snapshot);
      const identity = snapshotIdentity(snapshot);
      if (seenIdentities.has(identity)) {
        throw new TypeError(
          seenIdentities.get(identity) === content
            ? "Fish Guide evidence snapshot is duplicated"
            : "Fish Guide evidence identity collision",
        );
      }
      seenIdentities.set(identity, content);
      return { id, snapshot };
    });
    const sorted = evidence.slice().sort((left, right) => left.id.localeCompare(right.id));
    if (evidence.some((entry, index) => entry.id !== sorted[index].id)) {
      throw new TypeError("Fish Guide evidence rows must use canonical ID order");
    }
    return {
      kind: "ready",
      raw,
      document: {
        schema: EVIDENCE_SCHEMA,
        revision: positiveRevision(document.revision, "Fish Guide evidence"),
        evidence,
      },
    };
  } catch (error) {
    return invalid(raw, String(error?.message || error));
  }
}

function decodeActive(raw) {
  const parsed = parseJson(raw, "Fish Guide active reference", MAX_ACTIVE_RAW_CHARS);
  if (parsed.kind !== "parsed") {
    return parsed;
  }
  if (parsed.value.schema !== ACTIVE_SCHEMA) {
    return unsupported(raw, "Fish Guide active-reference schema is unsupported");
  }
  try {
    const document = exactRecord(
      parsed.value,
      ["schema", "revision", "evidenceId"],
      "Fish Guide active reference",
    );
    return {
      kind: "ready",
      raw,
      document: {
        schema: ACTIVE_SCHEMA,
        revision: positiveRevision(document.revision, "Fish Guide active reference"),
        evidenceId: evidenceId(document.evidenceId, { nullable: true }),
      },
    };
  } catch (error) {
    return invalid(raw, String(error?.message || error));
  }
}

function componentRevision(component) {
  return component.kind === "ready" ? component.document.revision : 0;
}

function composeProjection(evidenceState, activeState, { authoritative = null } = {}) {
  const evidenceRevision = componentRevision(evidenceState);
  const activeRevision = componentRevision(activeState);
  const raw = {
    evidence: evidenceState.raw ?? null,
    activeReference: activeState.raw ?? null,
  };
  const failed = [evidenceState, activeState].find(
    (candidate) => candidate.kind === "unsupportedVersion",
  ) ?? [evidenceState, activeState].find((candidate) => candidate.kind === "invalid");
  if (failed) {
    return frozenClone({
      kind: failed.kind,
      revision: `e${evidenceRevision}|a${activeRevision}`,
      evidenceRevision,
      activeRevision,
      activeEvidenceId: null,
      fishGuideProgress: null,
      evidence: evidenceState.kind === "ready" ? evidenceState.document.evidence : null,
      raw,
      reason: failed.reason ?? null,
      authoritative,
    });
  }

  const activeEvidenceId = activeState.kind === "ready"
    ? activeState.document.evidenceId
    : null;
  const rows = evidenceState.kind === "ready" ? evidenceState.document.evidence : [];
  const activeEvidence = activeEvidenceId === null
    ? null
    : rows.find((entry) => entry.id === activeEvidenceId) ?? null;
  if (activeEvidenceId !== null && activeEvidence === null) {
    return frozenClone({
      kind: "invalid",
      revision: `e${evidenceRevision}|a${activeRevision}`,
      evidenceRevision,
      activeRevision,
      activeEvidenceId,
      fishGuideProgress: null,
      evidence: rows,
      raw,
      reason: "Fish Guide active reference does not resolve to immutable evidence",
      authoritative,
    });
  }

  return frozenClone({
    kind: evidenceState.kind === "missing" && activeState.kind === "missing"
      ? "missing"
      : "ready",
    revision: `e${evidenceRevision}|a${activeRevision}`,
    evidenceRevision,
    activeRevision,
    activeEvidenceId,
    fishGuideProgress: activeEvidence === null
      ? { status: "unknown", snapshot: null }
      : { status: "available", snapshot: activeEvidence.snapshot },
    evidence: rows,
    raw,
    reason: null,
    authoritative,
  });
}

function errorProjection(kind, reason, authoritative) {
  return frozenClone({
    kind,
    revision: authoritative?.revision ?? null,
    evidenceRevision: authoritative?.evidenceRevision ?? null,
    activeRevision: authoritative?.activeRevision ?? null,
    activeEvidenceId: authoritative?.activeEvidenceId ?? null,
    fishGuideProgress: authoritative?.fishGuideProgress ?? null,
    evidence: authoritative?.evidence ?? null,
    raw: authoritative?.raw ?? { evidence: null, activeReference: null },
    reason,
    authoritative,
  });
}

export function createFishydexGuideEvidenceRepository({
  storage,
  eventTarget = globalThis.window,
  fishStateContract = globalThis.window?.__fishystuffSharedFishState,
} = {}) {
  const provider = requireStorage(fishydexBrowserStorage(storage));
  if (typeof fishStateContract?.normalizeFishGuideSnapshot !== "function") {
    throw new TypeError("Fish Guide snapshot contract is unavailable");
  }
  const listeners = new Set();
  let components = {
    evidence: { kind: "uninitialized", raw: null },
    active: { kind: "uninitialized", raw: null },
  };
  let state = frozenClone({
    kind: "uninitialized",
    revision: null,
    evidenceRevision: null,
    activeRevision: null,
    activeEvidenceId: null,
    fishGuideProgress: null,
    evidence: null,
    raw: { evidence: null, activeReference: null },
    reason: null,
    authoritative: null,
  });
  let loaded = false;
  let disposed = false;

  function alive() {
    if (disposed) {
      throw new Error("Fish Guide evidence repository is disposed");
    }
  }

  function publish(next = state) {
    state = next;
    for (const listener of listeners) {
      listener(state);
    }
    return state;
  }

  function read() {
    alive();
    const previous = ["ready", "missing"].includes(state.kind) ? state : state.authoritative;
    try {
      components = {
        evidence: decodeEvidence(provider.getItem(FISHYDEX_GUIDE_EVIDENCE_KEY), fishStateContract),
        active: decodeActive(provider.getItem(FISHYDEX_GUIDE_ACTIVE_REFERENCE_KEY)),
      };
      state = composeProjection(components.evidence, components.active);
    } catch (error) {
      state = errorProjection("unavailable", String(error?.message || error), previous ?? null);
    }
    loaded = true;
    return state;
  }

  function load() {
    alive();
    return loaded && state.kind !== "conflict" && state.kind !== "unavailable"
      ? state
      : read();
  }

  function unavailable(error, authoritative) {
    return publish(errorProjection(
      "unavailable",
      String(error?.message || error),
      authoritative,
    ));
  }

  async function ensure(snapshotValue, options = {}) {
    alive();
    const command = commandOptions(
      options,
      "Fish Guide evidence ensure options",
      "fishydex-guide-evidence",
    );
    const snapshot = boundedSnapshot(
      fishStateContract.normalizeFishGuideSnapshot(snapshotValue),
    );
    const authoritative = read();
    if (!["missing", "ready"].includes(authoritative.kind)) {
      return commandResult(authoritative, { operationId: command.operationId });
    }
    const document = components.evidence.kind === "ready"
      ? clone(components.evidence.document)
      : { schema: EVIDENCE_SCHEMA, revision: 0, evidence: [] };
    const identity = snapshotIdentity(snapshot);
    const existing = document.evidence.find(
      (entry) => snapshotIdentity(entry.snapshot) === identity,
    );
    if (existing) {
      if (canonicalSnapshot(existing.snapshot) !== canonicalSnapshot(snapshot)) {
        throw new TypeError("Fish Guide evidence identity collision");
      }
      // A repeated ensure is an idempotent acknowledgement even if its
      // originally observed collection revision is now stale.
      return commandResult(authoritative, {
        evidenceId: existing.id,
        operationId: command.operationId,
      });
    }
    if (
      command.expectedRevision !== undefined
      && command.expectedRevision !== authoritative.evidenceRevision
    ) {
      const conflict = publish(errorProjection(
        "conflict",
        "Fish Guide evidence collection revision changed",
        authoritative,
      ));
      return commandResult(conflict, {
        operationId: command.operationId,
        expectedRevision: command.expectedRevision,
        actualRevision: authoritative.evidenceRevision,
      });
    }
    if (document.evidence.length >= MAX_EVIDENCE_ROWS) {
      throw new TypeError(
        `Fish Guide evidence exceeds the v1 ${MAX_EVIDENCE_ROWS}-row limit`,
      );
    }
    let id = null;
    for (let attempt = 0; attempt < 4 && id === null; attempt += 1) {
      const candidate = createEvidenceId();
      if (!document.evidence.some((entry) => entry.id === candidate)) {
        id = candidate;
      }
    }
    if (id === null) {
      throw new Error("Unable to allocate a unique Fish Guide evidence ID");
    }
    document.evidence.push({ id, snapshot });
    document.evidence.sort((left, right) => left.id.localeCompare(right.id));
    document.revision += 1;
    const encoded = JSON.stringify(document);
    if (encoded.length > MAX_EVIDENCE_RAW_CHARS) {
      throw new TypeError("Fish Guide evidence exceeds its v1 raw-size limit");
    }
    try {
      const before = components.evidence.raw ?? null;
      if (provider.getItem(FISHYDEX_GUIDE_EVIDENCE_KEY) !== before) {
        const changed = read();
        const conflict = publish(errorProjection(
          "conflict",
          "Fish Guide evidence collection changed before acknowledgement",
          changed,
        ));
        return commandResult(conflict, {
          evidenceId: id,
          operationId: command.operationId,
          expectedRevision: authoritative.evidenceRevision,
          actualRevision: changed.evidenceRevision,
        });
      }
      provider.setItem(FISHYDEX_GUIDE_EVIDENCE_KEY, encoded);
      verifyStoredValue(provider, FISHYDEX_GUIDE_EVIDENCE_KEY, encoded);
    } catch (error) {
      return commandResult(unavailable(error, authoritative), {
        evidenceId: id,
        operationId: command.operationId,
      });
    }
    const next = read();
    publish(next);
    if (
      next.kind !== "ready"
      || !next.evidence.some((entry) => entry.id === id)
    ) {
      const conflict = publish(errorProjection(
        "conflict",
        "Fish Guide evidence write was not acknowledged by the authoritative collection",
        next,
      ));
      return commandResult(conflict, {
        evidenceId: id,
        operationId: command.operationId,
      });
    }
    return commandResult(next, {
      evidenceId: id,
      operationId: command.operationId,
    });
  }

  function resolve(rawEvidenceId) {
    alive();
    const id = evidenceId(rawEvidenceId);
    const authoritative = read();
    if (!["missing", "ready"].includes(authoritative.kind)) {
      return authoritative;
    }
    const row = authoritative.evidence?.find((entry) => entry.id === id) ?? null;
    return frozenClone({
      kind: row ? "ready" : "missing",
      revision: authoritative.revision,
      evidenceRevision: authoritative.evidenceRevision,
      evidenceId: id,
      snapshot: row?.snapshot ?? null,
      reason: row ? null : "Fish Guide evidence ID is missing",
    });
  }

  async function activate(rawEvidenceId, options = {}) {
    alive();
    const command = commandOptions(
      options,
      "Fish Guide activation options",
      "fishydex-guide-activation",
    );
    const id = evidenceId(rawEvidenceId, { nullable: true });
    const authoritative = read();
    if (!["missing", "ready"].includes(authoritative.kind)) {
      return commandResult(authoritative, { operationId: command.operationId });
    }
    if (
      components.active.kind === "ready"
      && components.active.document.evidenceId === id
    ) {
      // Desired-state retries acknowledge the already-applied activation.
      return commandResult(authoritative, { operationId: command.operationId });
    }
    if (command.expectedRevision !== undefined) {
      if (command.expectedRevision !== authoritative.activeRevision) {
        const conflict = publish(errorProjection(
          "conflict",
          "Fish Guide active reference revision changed",
          authoritative,
        ));
        return commandResult(conflict, {
          operationId: command.operationId,
          expectedRevision: command.expectedRevision,
          actualRevision: authoritative.activeRevision,
        });
      }
    }
    if (id !== null && !authoritative.evidence?.some((entry) => entry.id === id)) {
      return commandResult(authoritative, {
        kind: "missing",
        missingEvidenceId: id,
        reason: "Fish Guide evidence ID is missing",
        operationId: command.operationId,
      });
    }
    const document = {
      schema: ACTIVE_SCHEMA,
      revision: authoritative.activeRevision + 1,
      evidenceId: id,
    };
    const encoded = JSON.stringify(document);
    if (encoded.length > MAX_ACTIVE_RAW_CHARS) {
      throw new TypeError("Fish Guide active reference exceeds its v1 raw-size limit");
    }
    try {
      const before = components.active.raw ?? null;
      if (provider.getItem(FISHYDEX_GUIDE_ACTIVE_REFERENCE_KEY) !== before) {
        const changed = read();
        const conflict = publish(errorProjection(
          "conflict",
          "Fish Guide active reference changed before acknowledgement",
          changed,
        ));
        return commandResult(conflict, {
          operationId: command.operationId,
          expectedRevision: authoritative.activeRevision,
          actualRevision: changed.activeRevision,
        });
      }
      provider.setItem(FISHYDEX_GUIDE_ACTIVE_REFERENCE_KEY, encoded);
      verifyStoredValue(provider, FISHYDEX_GUIDE_ACTIVE_REFERENCE_KEY, encoded);
    } catch (error) {
      return commandResult(unavailable(error, authoritative), {
        operationId: command.operationId,
      });
    }
    const next = read();
    publish(next);
    return commandResult(next, { operationId: command.operationId });
  }

  async function reset() {
    alive();
    const authoritative = read();
    try {
      // Remove the mutable pointer first so a partial reset cannot leave a
      // persisted reference to evidence that has already been removed.
      removeAndVerify(provider, FISHYDEX_GUIDE_ACTIVE_REFERENCE_KEY);
      removeAndVerify(provider, FISHYDEX_GUIDE_EVIDENCE_KEY);
    } catch (error) {
      const recovered = read();
      return unavailable(error, recovered ?? authoritative);
    }
    const next = read();
    return publish(next);
  }

  function exportRaw() {
    alive();
    try {
      const records = [
        {
          name: "evidence",
          key: FISHYDEX_GUIDE_EVIDENCE_KEY,
          raw: provider.getItem(FISHYDEX_GUIDE_EVIDENCE_KEY),
        },
        {
          name: "activeReference",
          key: FISHYDEX_GUIDE_ACTIVE_REFERENCE_KEY,
          raw: provider.getItem(FISHYDEX_GUIDE_ACTIVE_REFERENCE_KEY),
        },
      ];
      return frozenClone({
        kind: records.every((entry) => entry.raw === null) ? "missing" : "ready",
        records,
      });
    } catch (error) {
      return frozenClone({
        kind: "unavailable",
        records: [],
        reason: String(error?.message || error),
      });
    }
  }

  function refresh() {
    if (!loaded || disposed) {
      return;
    }
    publish(read());
  }

  function onStorage(event) {
    if (event?.storageArea && event.storageArea !== provider) {
      return;
    }
    if (
      event?.key === null
      || event?.key === FISHYDEX_GUIDE_EVIDENCE_KEY
      || event?.key === FISHYDEX_GUIDE_ACTIVE_REFERENCE_KEY
    ) {
      refresh();
    }
  }

  function onPageShow(event) {
    if (event?.persisted === true) {
      refresh();
    }
  }

  eventTarget?.addEventListener?.("storage", onStorage);
  eventTarget?.addEventListener?.("pageshow", onPageShow);

  return Object.freeze({
    [FISHYDEX_GUIDE_EVIDENCE_BRAND]: 1,
    load,
    state() {
      alive();
      return state;
    },
    ensure,
    resolve,
    activate,
    reset,
    exportRaw,
    subscribe(listener) {
      alive();
      if (typeof listener !== "function") {
        throw new TypeError("Fish Guide evidence subscriber must be a function");
      }
      listeners.add(listener);
      if (loaded) {
        listener(state);
      }
      return () => listeners.delete(listener);
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      listeners.clear();
      eventTarget?.removeEventListener?.("storage", onStorage);
      eventTarget?.removeEventListener?.("pageshow", onPageShow);
    },
  });
}

export function browserFishydexGuideEvidenceRepository(options = {}) {
  const windowRef = options.window ?? globalThis.window;
  const occupied = windowRef?.[SLOT];
  if (occupied !== undefined) {
    if (occupied?.[FISHYDEX_GUIDE_EVIDENCE_BRAND] !== 1) {
      throw new TypeError("Shared Fish Guide evidence repository is incompatible");
    }
    return occupied;
  }
  const repository = createFishydexGuideEvidenceRepository({
    storage: fishydexBrowserStorage(options.storage),
    eventTarget: options.eventTarget ?? windowRef,
    fishStateContract: options.fishStateContract
      ?? windowRef?.__fishystuffSharedFishState,
  });
  if (windowRef && typeof windowRef === "object") {
    windowRef[SLOT] = repository;
  }
  return repository;
}
